import { Link } from "react-router-dom";
import { ArrowLeft, Mail, MapPin, Phone, FileText, Download, Info } from "lucide-react";
import { PolicyPageShell } from "@/components/PolicyHeader";
import { safeHtml } from "@/lib/sanitize";
import { useI18n } from "@/lib/i18n";

/**
 * PolicyDocPage — shared layout for long-form legal documents (Privacy,
 * Terms, Refund). Renders a clean, readable document with:
 *  - Eyebrow + title
 *  - Legal-notice banner clarifying English is the authoritative version
 *  - Lead paragraph
 *  - Numbered sections (each with optional sub-bullets)
 *  - Contact card at the bottom
 *  - "Last updated" line
 *
 * The `authoritative` banner is ALWAYS visible (Phase A) — Gujarati/Hindi
 * legal translations are handed to counsel for review before they can
 * legally supersede English. The banner text is translated so a
 * Gujarati/Hindi visitor is told, in their own language, that only the
 * English version currently governs.
 */
export default function PolicyDocPage({
  eyebrow,
  title,
  intro,
  sections, // [{ n, title, paragraphs?: [], bullets?: [], sub?: [{label, value}] }]
  lastUpdated,
  testId,
  contactName = "Mr. Digant Gamit, Head of Operations",
  contactEmail = "sahyogpfir@gmail.com",
  address = "A-106, Ambaji Complex, Near Unai Naka, Vyara, Dist. Tapi, Gujarat – 394650",
}) {
  const { t, lang } = useI18n();
  return (
    <PolicyPageShell testId={testId} eyebrow={eyebrow} title={title} lead={intro}>
      {/* Authoritative-English notice (Phase A). Rendered above the TOC so
          it is unmissable but non-disruptive; never blocks the flow. */}
      <div
        data-testid="policy-legal-notice"
        role="note"
        aria-label="Legal notice"
        className="mb-6 rounded-2xl border border-[#D4AF37]/30 bg-[#0A1F44]/50 backdrop-blur px-4 sm:px-5 py-3 sm:py-4 flex items-start gap-3"
      >
        <Info size={16} className="text-[#F0D788] mt-0.5 shrink-0" aria-hidden="true" />
        <div className="min-w-0 text-[12px] sm:text-[13px] text-white/75 leading-relaxed">
          <div className="uppercase tracking-[0.22em] text-[10px] text-[#D4AF37] font-semibold mb-1">
            {t("legal.notice.label")}
          </div>
          <div className="[overflow-wrap:anywhere]">
            {t("legal.notice.english_authoritative")}
          </div>
        </div>
      </div>

      {/* Section navigation */}
      <nav
        data-testid="policy-doc-toc"
        className="mb-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-2"
      >
        {sections.map((s) => (
          <a
            key={s.n}
            href={`#sec-${s.n}`}
            data-testid={`policy-toc-${s.n}`}
            className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-[#D4AF37]/40 transition group"
          >
            <span className="text-xs text-white/75 group-hover:text-white">
              <span className="text-[#D4AF37] font-semibold mr-2">{String(s.n).padStart(2, "0")}</span>
              {s.title}
            </span>
            <FileText size={12} className="text-[#F0D788] shrink-0" />
          </a>
        ))}
      </nav>

      {/* Sections */}
      <div className="space-y-10">
        {sections.map((s) => (
          <section
            key={s.n}
            id={`sec-${s.n}`}
            data-testid={`policy-section-${s.n}`}
            className="scroll-mt-32"
          >
            <div className="flex items-baseline gap-3 mb-3">
              <div className="font-serif text-2xl text-[#F0D788]">
                {String(s.n).padStart(2, "0")}
              </div>
              <h2 className="font-serif text-xl md:text-2xl text-white leading-tight">
                {s.title}
              </h2>
            </div>
            {s.paragraphs?.map((p, i) => (
              <p
                key={`p-${i}-${String(p).slice(0, 24)}`}
                className="text-sm md:text-base text-white/80 leading-relaxed mb-3"
                dangerouslySetInnerHTML={safeHtml(p)}
              />
            ))}
            {s.bullets && (
              <ul className="space-y-1.5 mt-2 mb-2">
                {s.bullets.map((b, i) => (
                  <li
                    key={`b-${i}-${String(b).slice(0, 24)}`}
                    className="flex items-start gap-3 text-sm md:text-base text-white/80"
                  >
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#D4AF37] shrink-0" />
                    <span dangerouslySetInnerHTML={safeHtml(b)} />
                  </li>
                ))}
              </ul>
            )}
            {s.sub && (
              <div className="mt-3 grid sm:grid-cols-2 gap-2">
                {s.sub.map((row, i) => (
                  <div
                    key={`sub-${i}-${(row?.label || "").slice(0, 24)}`}
                    className="rounded-xl border border-[#D4AF37]/15 bg-[#0A1F44] px-4 py-3"
                  >
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1">
                      {row.label}
                    </div>
                    <div className="text-sm text-white/85">{row.value}</div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      {/* Contact card */}
      <section className="mt-12 rounded-2xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#0A1F44] to-[#04102B] p-7 md:p-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">
          Grievance Redressal · Direct Contact
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-sm text-white font-semibold mb-1">{contactName}</div>
            <a
              href={`mailto:${contactEmail}`}
              data-testid="policy-doc-email"
              className="flex items-center gap-2 text-sm text-white/85 hover:text-[#F0D788]"
            >
              <Mail size={14} className="text-[#D4AF37]" /> {contactEmail}
            </a>
          </div>
          <div className="flex items-start gap-2 text-sm text-white/75">
            <MapPin size={14} className="text-[#D4AF37] mt-1 shrink-0" />
            <span>{address}</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <div className="mt-8 flex flex-wrap items-center justify-between gap-3 text-xs text-white/55">
        <div>Last updated: <strong className="text-white/80">{lastUpdated}</strong></div>
        <Link
          to="/policies"
          className="inline-flex items-center gap-2 text-[#F0D788] hover:underline"
        >
          <ArrowLeft size={12} /> Back to Policy Hub
        </Link>
      </div>
    </PolicyPageShell>
  );
}
